import React, { useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import cx from 'clsx';
import { Navigation as NavigationType } from '../../util/types';
import colors from '../../util/colors';
import classes from './Navigation.module.scss';

const Link = dynamic(() => import('next/link'));
const Image = dynamic(() => import('next/image'));
const NavigationButton = dynamic(() => import('./NavigationButton/NavigationButton'));
const Text = dynamic(() => import('../../common/Text/Text'));
const Container = dynamic(() => import('../../common/Container/Container'));
const Button = dynamic(() => import('../../common/Button/Button'));
const MobileMenu = dynamic(() => import('../MobileMenu/MobileMenu'));
const UserIcon = dynamic(() => import('../../common/UserIcon/UserIcon'));

const default_logo = 'https://storagy-advent-core-ppr.s3.amazonaws.com/storage/aa2d02cb-f501-4f62-96cb-a0486086ee0e.svg';

export interface NavigationProps extends React.HTMLProps<HTMLDivElement> {
  navigation?: NavigationType;
}

export default function Navigation({
  className,
  navigation,
  ...others
}: NavigationProps) {
  const router = useRouter();

  const [activeItem, setActiveItem] = useState(navigation?.menu?.find(item => item.fields.withLinks));
  const [activeItemIndex, setActiveItemIndex] = useState(navigation?.menu?.findIndex(item => item.fields.withLinks));
  const [activeSecondIndex, setActiveSecondIndex] = useState(3);
  const [hoverItem, setHoverItem] = useState(navigation?.menu?.find(item => item.fields.withLinks));
  const [hoverItemIndex, setHoverItemIndex] = useState(navigation?.menu?.findIndex(item => item.fields.withLinks));
  const [showMobileNavigation, setShowMobileNavigation] = useState(false);

  return (
    <nav
      className={cx(className, classes.navigation, { [classes.results]: router.pathname.includes('results') })}
      onMouseLeave={() => {
        setHoverItem(activeItem);
        setHoverItemIndex(activeItemIndex);
      }}
      {...others}
    >
      <Container
        className={cx(classes.navigationTop, classes.screenView, {
          [classes.withBottom]:
          activeItem?.fields.withLinks && activeItem?.fields.children && activeItem?.fields.children?.length > 0,
        })}
      >
        <div className={classes.navigationTopLeft}>
          <Link href={navigation?.logo_url || '/'} passHref>
            <a className={classes.logo}>
              <Image
                className={classes.logoImage}
                src={navigation?.logo?.fields.file.url ?
                  navigation?.logo?.fields.file.url.slice(0, 2) === '//' ?
                    `https:${navigation?.logo?.fields.file.url}` : navigation?.logo?.fields.file.url :
                  default_logo
                }
                placeholder="empty"
                alt="logo"
                style={{ objectFit: 'contain' }}
                width={navigation?.logo?.fields.file.details.image.width}
                height={navigation?.logo?.fields.file.details.image.height}
                priority={true}
              />
            </a>
          </Link>
          <div className={classes.menuButtons}>
            {navigation?.menu.map((item, index) => (
              <NavigationButton
                key={index}
                className={classes.menuButton}
                onMouseEnter={() => {
                  setHoverItem(item);
                  setHoverItemIndex(index);
                }}
                onClick={() => {
                  setActiveItemIndex(index);
                  setActiveItem(item);
                }}
                active={index === activeItemIndex}
                {...item.fields}
              />
            ))}
          </div>
        </div>
        <div className={classes.navigationTopRight}>
          <Link href={navigation?.roi.fields.href || ''} passHref>
            <a className={classes.roi}><Text bold>{navigation?.roi.fields.children}</Text></a>
          </Link>
          <Button
            className={classes.demoButton}
            component="a"
            href={navigation?.request_demo.fields.href}
            target={navigation?.request_demo.fields.target}
            data-tracking-key="locator_header_request_demo"
          >
            <Text bold>{navigation?.request_demo.fields.children}</Text>
          </Button>
          <UserIcon
            iconBackground={colors.purple}
            iconSize={16}
            iconColor={colors.teal}
            menuItems={navigation?.userMenu}
          />
        </div>
      </Container>
      <div className={cx(classes.navigationBottom, classes.screenView)}>
        <Container className={classes.navigationBottomInner}>
          <div className={classes.emptyLogo} />
          {hoverItem?.fields.children?.map((item, index) => (
            <Link href={item.fields.href} passHref key={index}>
              <a className={classes.bottomLink}>
                <Text
                  className={cx(classes.bottomText, {
                    [classes.bottomActive]: hoverItemIndex === activeItemIndex ? index === activeSecondIndex : false,
                  })}
                  bold
                >
                  {item.fields.children}
                </Text>
              </a>
            </Link>
          ))}
        </Container>
      </div>

      <Container className={cx(classes.mobileView, { [classes.mobileResults]: router.pathname.includes('results') })}>
        <Button
          className={classes.mobileNavigationButton}
          onClick={() => setShowMobileNavigation(!showMobileNavigation)}
          aria-label="Toggle menu button"
        >
          <div className={classes.menuIcon}>
            {[...Array(3).keys()].map((_, index) => (
              <div key={index} className={classes.menuLine} />
            ))}
          </div>
        </Button>
        <Link href={navigation?.logo_url || '/'} passHref>
          <a className={classes.mobileLogo}>
            <Image
              priority={true}
              className={classes.logoImage}
              src={navigation?.logo?.fields.file.url ?
                navigation?.logo?.fields.file.url.slice(0, 2) === '//' ?
                  `https:${navigation?.logo?.fields.file.url}` : navigation?.logo?.fields.file.url :
                default_logo
              }
              placeholder="empty"
              alt="logo"
              style={{ objectFit: 'contain' }}
              width={navigation?.logo?.fields.file.details.image.width}
              height={navigation?.logo?.fields.file.details.image.height}
            />
          </a>
        </Link>
        <Button
          className={classes.demoButton} href={navigation?.request_demo.fields.href}
          target={navigation?.request_demo.fields.target}
          data-tracking-key="locator_header_request_demo"
        >
          <Text bold>{navigation?.request_demo.fields.children}</Text>
        </Button>
        <MobileMenu opened={showMobileNavigation} menu={navigation?.menu} roi={navigation?.roi} />
      </Container>
    </nav>
  );
}

Navigation.displayName = '@ioa/lablocator/Navigation';
